import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { Modal, Button, DateRange } from "../../../ui/index.ts";

import styles from "./custom-period-allocation-modal.module.css";

export type CustomPeriodAllocationModalProps = {
  open: boolean;
  /** When set, modal can render (project has members). Dates only — no API here. */
  hasProject: boolean;
  onConfirmDates: (startDateIso: string, endDateIso: string) => void;
  onCancel: () => void;
};

function toIsoDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function CustomPeriodAllocationModal({
  open,
  hasProject,
  onConfirmDates,
  onCancel,
}: CustomPeriodAllocationModalProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!open) return;
    setStartDate(null);
    setEndDate(null);
  }, [open]);

  const handleRangeChange = (dates: [Date | null, Date | null]) => {
    setStartDate(dates[0]);
    setEndDate(dates[1]);
  };

  const handleConfirm = () => {
    if (!startDate || !endDate) {
      toast.error("Select a date range.");
      return;
    }
    if (endDate < startDate) {
      toast.error("End date must be on or after start date.");
      return;
    }
    onConfirmDates(toIsoDateString(startDate), toIsoDateString(endDate));
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Custom"
      size="md"
      minHeight="25rem"
      footer={
        <div className={styles.footerActions}>
          <Button type="button" variant="muted-outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={handleConfirm}
            disabled={!hasProject}
          >
            Confirm
          </Button>
        </div>
      }
    >
      {!hasProject ? (
        <p className={`body-sm ${styles.emptyHint}`}>No members on this project.</p>
      ) : (
        <div className={styles.form}>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleRangeChange}
            placeholderText="Select Date Range"
          />
        </div>
      )}
    </Modal>
  );
}
